import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import { api, useAuth } from "@/contexts/AuthContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import {
  ArrowLeft, Building2, User, ClipboardCheck, Package,
  PlayCircle, ArrowRight, RotateCcw, CheckCircle2, XCircle, MinusCircle,
  History, AlertTriangle, Mail, Send, Landmark, Clock, Users as UsersIcon, DollarSign, Sparkles, Trash2, Download, ChevronDown, ChevronUp,
} from "lucide-react";

const STAGE_ORDER = ["pending_program_selection", "received", "in_production", "testing", "agent_review", "ready_for_install", "installed"];
const STAGE_LABEL = { pending_program_selection: "Program Selection", received: "Received", in_production: "In Production", testing: "Testing", agent_review: "Agent Review", ready_for_install: "Ready for Install", installed: "Installed" };
import OnboardingTracker from "@/components/OnboardingTracker";
import ReassignDealerDialog from "@/components/ReassignDealerDialog";
import DealerProgressHeader from "@/components/DealerProgressHeader";
import CorrectionsPanel from "@/components/CorrectionsPanel";
import InstallPreviewPanel from "@/components/InstallPreviewPanel";
import DealerFilePanel from "@/components/DealerFilePanel";
import OnboardingMessages from "@/components/OnboardingMessages";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "@/components/ui/dialog";

const STATUS_META = {
  pending_program_selection: { label: "Pending Program Selection", tone: "bg-[#F1F3F5] text-[#6C757D] border-[#E9ECEF]" },
  received:                  { label: "Received",                  tone: "bg-blue-50 text-blue-700 border-blue-200" },
  in_production:             { label: "In Production",             tone: "bg-orange-50 text-orange-700 border-orange-200" },
  testing:                   { label: "Testing",                   tone: "bg-purple-50 text-purple-700 border-purple-200" },
  agent_review:              { label: "Agent Review",              tone: "bg-[#FFF8E1] text-[#8A6D1F] border-[#D4AF37]/60" },
  ready_for_install:         { label: "Ready for Install",         tone: "bg-yellow-50 text-yellow-800 border-yellow-200" },
  dealer_install:            { label: "Dealer Install",            tone: "bg-yellow-50 text-yellow-800 border-yellow-200" },
  install_complete:          { label: "Install Complete",          tone: "bg-green-50 text-green-700 border-green-200" },
  archived:                  { label: "Archived",                  tone: "bg-slate-200 text-slate-700 border-slate-300" },
};
const INSTALL_STATUSES = [
  { key: "not_started", label: "Not Started" },
  { key: "in_progress", label: "In Progress" },
  { key: "installed",   label: "Installed" },
  { key: "needs_review",label: "Needs Review" },
];

export default function OnboardingDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const [rec, setRec] = useState(null);
  const [busy, setBusy] = useState(false);
  const [installForm, setInstallForm] = useState({ install_date: "", training_date: "", notes: "", dealer_contacts_trained: "" });
  const [returnDlg, setReturnDlg] = useState({ open: false, reason: "" });
  const [moveDlg, setMoveDlg] = useState({ open: false, to: "", reason: "", mode: "keep", override: false });
  const [activityOpen, setActivityOpen] = useState(false);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [subagentCommission, setSubagentCommission] = useState("");
  const [subagentCommissionNotes, setSubagentCommissionNotes] = useState("");
  const [agencyCommission, setAgencyCommission] = useState("");

  const load = async () => {
    try {
      const { data } = await api.get(`/onboarding/${id}`);
      setRec(data);
      setInstallForm({
        install_date: data.install_date || "",
        training_date: data.training_date || "",
        notes: data.dealer_install_notes || "",
        dealer_contacts_trained: data.dealer_contacts_trained || "",
      });
    } catch (e) { toast.error(e.response?.data?.detail || "Failed to load"); }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [id]);

  const doAction = async (label, fn) => {
    if (busy) return;
    setBusy(true);
    try { await fn(); await load(); toast.success(label); }
    catch (e) { toast.error(e.response?.data?.detail || `${label} failed`); }
    finally { setBusy(false); }
  };

  const isAdmin = user?.role === "admin";
  const isAgent = rec && (rec.signing_agent_id === user?.id || rec.assigned_install_user_id === user?.id);
  const isAgencyAdmin = user?.role === "agency_admin" && rec && rec.agency === user?.agency;
  const canReassign = isAdmin || isAgencyAdmin;

  const deleteOnboarding = async () => {
    if (!window.confirm(`Delete this onboarding record for "${rec?.dealer_name}"? This hard-deletes the pipeline entry + activity log. Cannot be undone (use to clear demo/training content).`)) return;
    try {
      await api.delete(`/onboarding/${id}`);
      toast.success(`Deleted · ${rec.dealer_name}`);
      nav("/onboarding");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Delete failed");
    }
  };
  const isCurrentOwner = rec && rec.signing_agent_id === user?.id;
  const isSubagentOwner = isCurrentOwner && (user?.agent_type === "subagent");
  const reviewStatus = rec?.commissions_review_status || "not_needed";
  const canSubmitCommissions = isSubagentOwner
    && ["pending_program_selection", "received"].includes(rec?.status)
    && (reviewStatus === "not_needed" || reviewStatus === "pending");
  const canSetAgencyCommission = (isAdmin || isAgencyAdmin)
    && ["pending", "set"].includes(reviewStatus);

  if (!rec) {
    return (
      <div className="min-h-screen bg-[#F8F9FA]">
        <Navbar />
        <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8"><div className="text-sm text-[#6C757D]">Loading…</div></main>
      </div>
    );
  }

  const meta = STATUS_META[rec.status] || STATUS_META.received;

  return (
    <div className="min-h-screen bg-[#F8F9FA]">
      <Navbar />
      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-8" data-testid="onboarding-detail-page">
        <Link to="/onboarding" className="inline-flex items-center gap-1.5 text-sm text-[#6C757D] hover:text-[#0B132B] mb-4">
          <ArrowLeft size={14}/> Back to pipeline
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4 mb-4">
          <div>
            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-[#D4AF37]">Onboarding record</div>
            <h1 className="font-display text-4xl font-black text-[#0B132B] leading-none mt-1">{rec.dealer_name || "Untitled dealer"}</h1>
            {rec.dealer_id && <Link to={`/dealers/${rec.dealer_id}/profile`} className="inline-block mt-1 text-xs underline text-[#0B132B]" data-testid="onboarding-dealer-profile-link">Open dealer profile →</Link>}
            <div className="mt-2 flex flex-wrap items-center gap-3 text-xs text-[#6C757D]">
              {rec.agency && <span className="flex items-center gap-1"><Building2 size={12}/> {rec.agency}</span>}
              {rec.signing_agent_name && <span className="flex items-center gap-1"><User size={12}/> {rec.signing_agent_name}</span>}
              <span className={`px-2 py-1 rounded-sm border text-[10px] uppercase tracking-[0.16em] font-bold ${meta.tone}`}>{meta.label}</span>
            </div>
          </div>
          {isAdmin && (
            <Button
              onClick={deleteOnboarding}
              variant="outline"
              size="sm"
              className="h-9 rounded-sm border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300"
              data-testid="onboarding-delete-btn"
              title="Hard-delete this onboarding record (use to clear demo/training content)"
            >
              <Trash2 size={13} className="mr-1.5" /> Delete record
            </Button>
          )}
        </div>

        {/* Phase-1 spec §10 — Dealer Progress Header (Target vs ETA vs Health) */}
        <DealerProgressHeader
          rec={rec}
          canEditTarget={isAdmin || isAgencyAdmin || isCurrentOwner}
          onChanged={load}
        />

        {/* Phase-3 — Corrections + Agent Review actions */}
        <CorrectionsPanel rec={rec} user={user} onChanged={load} />

        {/* Phase-B horizontal milestone tracker — primary at-a-glance status */}
        <Card className="p-5 border-[#E9ECEF] rounded-sm mb-4 bg-white" data-testid="tracker-card">
          <OnboardingTracker milestones={rec.milestones || []} onMove={isAdmin ? (to) => setMoveDlg({ open: true, to, reason: "", mode: "keep", override: false }) : undefined} />
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-5 pt-4 border-t border-[#F1F3F5]">
            <div data-testid="current-owner-block">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Current owner</div>
              <div className="text-sm font-bold text-[#0B132B] mt-0.5">{rec.current_owner?.name || "—"}</div>
              <div className="text-[10px] text-[#6C757D] italic">{rec.current_owner?.role_hint || ""}</div>
            </div>
            <div data-testid="next-action-block">
              <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Next action</div>
              <div className="text-sm font-bold text-[#0B132B] mt-0.5">{rec.next_action || "—"}</div>
              {rec.ach?.status === "verification_pending" && rec.status !== "install_complete" && (
                <div className="text-[10px] text-orange-700 italic mt-0.5">
                  ACH verification runs in parallel — install can still proceed
                </div>
              )}
            </div>
          </div>
        </Card>

        {/* Current stage + owner + Reassign — always visible, actions gated to admin/agency-admin */}
        <Card
          className="p-5 border-[#E9ECEF] rounded-sm mb-4 bg-white"
          data-testid="stage-owner-panel"
        >
          <div className="flex flex-wrap items-start justify-between gap-4">
            <div className="flex-1 min-w-[220px]">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">
                Currently in stage
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span
                  className={`px-2.5 py-1 rounded-sm border text-[10px] uppercase tracking-[0.18em] font-bold ${meta.tone}`}
                  data-testid="stage-owner-status-badge"
                >
                  {meta.label}
                </span>
              </div>
              <div className="text-[11px] text-[#6C757D] mt-2 italic max-w-md">
                {rec.next_action || "No action queued."}
              </div>
            </div>
            <div className="flex-1 min-w-[220px]">
              <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#6C757D]">
                Owner
              </div>
              <div className="mt-2 flex items-center gap-2">
                <User size={14} className="text-[#0B132B]" />
                <span
                  className="text-sm font-bold text-[#0B132B]"
                  data-testid="stage-owner-name"
                >
                  {rec.current_owner?.name || rec.signing_agent_name || "—"}
                </span>
              </div>
              {rec.agency && (
                <div className="text-[11px] text-[#6C757D] mt-1 flex items-center gap-1">
                  <Building2 size={11} /> {rec.agency}
                </div>
              )}
            </div>
            {canReassign && (
              <div className="flex flex-col gap-2">
                <Button
                  onClick={() => setReassignOpen(true)}
                  className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold"
                  data-testid="btn-reassign-dealer"
                >
                  <UsersIcon size={14} className="mr-1.5" /> Reassign dealer
                </Button>
                <div className="text-[10px] text-[#6C757D] italic text-right">
                  {isAdmin ? "All users available" : "Same agency only"}
                </div>
              </div>
            )}
          </div>
        </Card>

        {/* Ownership strip — internal users can claim / take over at any stage */}
        {(isAdmin || user?.role === "support" || (user?.permissions || []).includes("onboarding.production.manage")) && (
          <div className="mb-4 p-3 rounded-sm border border-[#D4AF37]/50 bg-[#D4AF37]/10 flex flex-wrap items-center justify-between gap-3" data-testid="ownership-strip">
            <div className="text-sm text-[#0B132B]">
              Production owner: <b data-testid="ownership-owner">{rec.assigned_production_user_name || "unassigned"}</b>
              {rec.assigned_production_user_id === user?.id && <span className="ml-2 text-[10px] uppercase tracking-[0.14em] font-bold text-emerald-700">· that's you</span>}
            </div>
            {rec.assigned_production_user_id !== user?.id && (
              <Button size="sm" onClick={() => doAction(rec.assigned_production_user_name ? "Ownership taken over" : "Ownership claimed", () => api.post(`/onboarding/${id}/claim-ownership`))}
                className="h-9 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold" data-testid="btn-claim-ownership">
                {rec.assigned_production_user_name ? `Take over from ${rec.assigned_production_user_name}` : "Take ownership"}
              </Button>
            )}
          </div>
        )}

        {/* Dealer file (agreement + commissions) — every stage, every viewer with access */}
        <DealerFilePanel onboardingId={id} />

        {/* Super-admin: install preview, assign-to-support, program corrections */}
        {isAdmin && (rec.products || []).length > 0 && (
          <InstallPreviewPanel onboardingId={id} onChanged={load} />
        )}

        {/* Support ↔ super-admin thread */}
        <OnboardingMessages onboardingId={id} />

        {/* Commissions review track — subagent submit + agency-admin approve */}
        {(canSubmitCommissions || canSetAgencyCommission || reviewStatus !== "not_needed") && (
          <Card
            className="p-5 border-[#E9ECEF] rounded-sm mb-4 bg-white"
            data-testid="commissions-review-panel"
          >
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-[10px] uppercase tracking-[0.22em] font-bold text-[#D4AF37]">
                  Commissions review
                </div>
                <div className="text-sm font-bold text-[#0B132B] mt-1">
                  {reviewStatus === "not_needed"  && (isSubagentOwner ? "Action needed — submit your commission markup" : "Ready — subagent must submit")}
                  {reviewStatus === "pending"     && (isSubagentOwner ? "Submitted — awaiting agency admin" : "Awaiting your review")}
                  {reviewStatus === "set"         && "Agency commission set — ready to approve"}
                  {reviewStatus === "approved"    && "Approved for production"}
                </div>
              </div>
              <span
                className={`px-2 py-1 rounded-sm border text-[10px] uppercase tracking-[0.18em] font-bold ${
                  reviewStatus === "approved" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                  : reviewStatus === "pending" ? "bg-yellow-50 text-yellow-800 border-yellow-200"
                  : reviewStatus === "set" ? "bg-blue-50 text-blue-700 border-blue-200"
                  : isSubagentOwner ? "bg-[#D4AF37]/15 text-[#0B132B] border-[#D4AF37]/40"
                  : "bg-[#F1F3F5] text-[#6C757D] border-[#E9ECEF]"
                }`}
                data-testid="commissions-review-status-badge"
              >
                {reviewStatus === "not_needed" && isSubagentOwner ? "action needed" : reviewStatus.replace("_", " ")}
              </span>
            </div>

            {/* Persistent visibility: subagent always sees their own submitted markup + notes */}
            {rec.subagent_commission_markup != null && (
              <div className="pt-3 border-t border-[#F1F3F5] grid grid-cols-1 md:grid-cols-2 gap-3">
                <div>
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Subagent markup</div>
                  <div className="text-sm font-bold text-[#0B132B] mt-1" data-testid="subagent-commission-value">
                    ${Number(rec.subagent_commission_markup).toFixed(2)}
                  </div>
                  {rec.commissions_submitted_by?.name && (
                    <div className="text-[10px] text-[#6C757D] italic mt-0.5">
                      by {rec.commissions_submitted_by.name}
                    </div>
                  )}
                </div>
                {rec.commissions_notes && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Notes</div>
                    <div className="text-sm text-[#0B132B] mt-1 italic">"{rec.commissions_notes}"</div>
                  </div>
                )}
              </div>
            )}

            {/* Subagent-only: markup input + submit (hidden after submit or approval) */}
            {canSubmitCommissions && reviewStatus === "not_needed" && (
              <div className="grid grid-cols-1 md:grid-cols-[1fr_1fr_auto] gap-3 items-end pt-3 border-t border-[#F1F3F5]">
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0B132B]">Your commission markup ($)</Label>
                  <Input
                    type="number"
                    step="0.01"
                    value={subagentCommission}
                    onChange={(e) => setSubagentCommission(e.target.value)}
                    placeholder="e.g. 50.00"
                    className="mt-2 h-10 rounded-sm"
                    data-testid="subagent-commission-input"
                  />
                </div>
                <div>
                  <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0B132B]">Notes (optional)</Label>
                  <Input
                    value={subagentCommissionNotes}
                    onChange={(e) => setSubagentCommissionNotes(e.target.value)}
                    placeholder="Context for the agency admin"
                    className="mt-2 h-10 rounded-sm"
                    data-testid="subagent-commission-notes-input"
                  />
                </div>
                <Button
                  onClick={() => doAction("Submitted for review", async () => {
                    if (!subagentCommission) throw new Error("Enter a commission markup");
                    return api.post(`/onboarding/${id}/submit-for-commissions-review`, {
                      subagent_commission_markup: Number(subagentCommission),
                      commissions_notes: subagentCommissionNotes || null,
                    });
                  })}
                  disabled={busy || !subagentCommission}
                  className="h-10 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold"
                  data-testid="btn-submit-commissions"
                >
                  <Send size={14} className="mr-1.5" /> Submit for review
                </Button>
              </div>
            )}

            {/* Agency-side visible ONLY to admin/agency-admin. Persists after approval so admins can audit. */}
            {(isAdmin || isAgencyAdmin) && (rec.agency_commission_markup != null || canSetAgencyCommission) && (
              <div className="pt-3 mt-3 border-t border-[#F1F3F5]">
                <div className="flex items-center gap-2 mb-2">
                  <Sparkles size={13} className="text-[#D4AF37]" />
                  <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0B132B]">
                    Agency block (never shown to the subagent)
                  </div>
                </div>
                {reviewStatus === "approved" && rec.agency_commission_markup != null ? (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#6C757D]">Agency markup (locked)</div>
                    <div className="text-sm font-bold text-[#0B132B] mt-1" data-testid="agency-commission-locked-value">
                      ${Number(rec.agency_commission_markup).toFixed(2)}
                    </div>
                    {rec.commissions_approved_by?.name && (
                      <div className="text-[10px] text-[#6C757D] italic mt-0.5">
                        Approved by {rec.commissions_approved_by.name}
                      </div>
                    )}
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_auto] gap-3 items-end">
                    <div>
                      <Label className="text-[10px] uppercase tracking-[0.18em] font-bold text-[#0B132B]">Agency commission markup ($)</Label>
                      <Input
                        type="number"
                        step="0.01"
                        value={agencyCommission !== "" ? agencyCommission : (rec.agency_commission_markup ?? "")}
                        onChange={(e) => setAgencyCommission(e.target.value)}
                        placeholder="e.g. 75.00"
                        className="mt-2 h-10 rounded-sm"
                        data-testid="agency-commission-input"
                      />
                    </div>
                    <Button
                      variant="outline"
                      onClick={() => doAction("Agency commission saved", async () => {
                        const val = agencyCommission !== "" ? agencyCommission : rec.agency_commission_markup;
                        if (val == null || val === "") throw new Error("Enter an agency commission");
                        return api.post(`/onboarding/${id}/set-agency-commission`, {
                          agency_commission_markup: Number(val),
                        });
                      })}
                      disabled={busy}
                      className="h-10 rounded-sm border-[#0B132B]/20"
                      data-testid="btn-set-agency-commission"
                    >
                      <DollarSign size={14} className="mr-1" /> Save
                    </Button>
                    <Button
                      onClick={() => doAction("Approved for production", () => api.post(`/onboarding/${id}/approve-commissions`, {}))}
                      disabled={busy || rec.agency_commission_markup == null}
                      className="h-10 rounded-sm bg-emerald-600 hover:bg-emerald-700 text-white font-semibold disabled:opacity-40"
                      data-testid="btn-approve-commissions"
                    >
                      <CheckCircle2 size={14} className="mr-1.5" /> Approve for production
                    </Button>
                  </div>
                )}
              </div>
            )}
          </Card>
        )}

        {/* --- IN PRODUCTION --- */}
        {rec.status === "received" && isAdmin && (
          <Card className="p-5 border-[#E9ECEF] rounded-sm mb-4 bg-orange-50 border-orange-200" data-testid="stage-received-actions">
            <div className="flex items-center justify-between gap-3">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-orange-900">Received</div>
                <div className="text-sm text-orange-800 mt-1">This dealer is waiting on the internal Production team. Click below to take ownership.</div>
              </div>
              <Button onClick={() => doAction("Onboarding started", () => api.post(`/onboarding/${id}/take-production`))}
                className="h-11 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold" data-testid="btn-take-production">
                <PlayCircle size={14} className="mr-2"/> Take / Start Onboarding
              </Button>
            </div>
          </Card>
        )}

        {rec.status === "in_production" && (
          <Card className="p-5 border-[#E9ECEF] rounded-sm mb-4" data-testid="stage-production-card">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-orange-700">In Production</div>
                <div className="text-[11px] text-[#6C757D] mt-1">Production Owner: <b>{rec.assigned_production_user_name || "—"}</b></div>
              </div>
              {isAdmin && (
                <Button onClick={() => doAction("Sent to testing", () => api.post(`/onboarding/${id}/send-to-testing`))}
                  disabled={busy || (rec.products || []).some(p => p.install_status !== "installed")}
                  className="h-11 rounded-sm bg-[#0B132B] hover:bg-[#1C2541] text-white font-semibold disabled:opacity-40" data-testid="btn-send-to-testing">
                  Send to Testing <ArrowRight size={14} className="ml-2"/>
                </Button>
              )}
            </div>
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] mb-2">Product install checklist</div>
            <div className="space-y-2">
              {(rec.products || []).map((p) => (
                <ProductRow key={p.line_id || p.product_id} onboardingId={id} product={p} editable={isAdmin} onSaved={load} />
              ))}
              {(rec.products || []).some((p) => p.product_key === "subscription_vsc") && (
                <SubscriptionUrlField
                  onboardingId={id}
                  initialUrl={rec.subscription_landing_url}
                  initialConfig={rec}
                  editable={isAdmin}
                  onSaved={load}
                />
              )}
            </div>
          </Card>
        )}

        {/* Phase-A ACH parallel-track section — only in Production */}
        {rec.status === "in_production" && (
          <AchSection rec={rec} onboardingId={id} editable={isAdmin} onSaved={load} />
        )}

        {/* Production rate cards linked to each selected program. Visible once
            programs are chosen, to admins + support + production. */}
        {(rec.products || []).length > 0 && (isAdmin || user?.role === "support" || (user?.permissions || []).includes("onboarding.production.manage")) && (
          <ProductionRateCardsPanel onboardingId={id} />
        )}

        {/* --- TESTING --- */}
        {rec.status === "testing" && (
          <Card className="p-5 border-[#E9ECEF] rounded-sm mb-4" data-testid="stage-testing-card">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-purple-700">Testing</div>
                <div className="text-[11px] text-[#6C757D] mt-1">Testing Owner: <b>{rec.assigned_testing_user_name || "unassigned"}</b></div>
              </div>
              <div className="flex gap-2">
                {isAdmin && !rec.assigned_testing_user_id && (
                  <Button onClick={() => doAction("Testing taken", () => api.post(`/onboarding/${id}/take-testing`))}
                    className="h-10 rounded-sm bg-purple-700 hover:bg-purple-800 text-white" data-testid="btn-take-testing">
                    Take Test
                  </Button>
                )}
                {isAdmin && (
                  <>
                    <Button variant="outline" onClick={() => setReturnDlg({ open: true, reason: "" })}
                      className="h-10 rounded-sm border-red-300 text-red-700 hover:bg-red-50" data-testid="btn-return-to-production">
                      <RotateCcw size={14} className="mr-1.5"/> Return to Production
                    </Button>
                    <Button onClick={async () => {
                      if (busy) return;
                      setBusy(true);
                      try { await api.post(`/onboarding/${id}/testing-complete`); await load(); toast.success("Testing complete"); }
                      catch (e) {
                        const d = e.response?.data?.detail || "";
                        if (e.response?.status === 400 && /checklist|failed/i.test(d)) setMoveDlg({ open: true, to: "agent_review", reason: "", mode: "keep", override: true, blocker: d });
                        else toast.error(d || "Testing complete failed");
                      } finally { setBusy(false); }
                    }} className="h-10 rounded-sm bg-green-700 hover:bg-green-800 text-white" data-testid="btn-testing-complete">
                      Testing Complete <ArrowRight size={14} className="ml-2"/>
                    </Button>
                  </>
                )}
              </div>
            </div>
            <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] mb-2">Checklist</div>
            <div className="space-y-2">
              {(rec.testing_checklist || []).map((it) => (
                <ChecklistRow key={it.key} onboardingId={id} item={it} editable={isAdmin && !!rec.assigned_testing_user_id} onSaved={load} />
              ))}
            </div>
          </Card>
        )}

        {/* --- AGENT REVIEW --- Final QA gate before install kickoff. */}
        {rec.status === "agent_review" && (
          <Card className="p-5 border-2 border-[#D4AF37]/60 bg-[#FFFDF5] rounded-sm mb-4" data-testid="stage-agent-review-card">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-3">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#8A6D1F]">Agent Review — Final QA</div>
                <div className="text-[11px] text-[#6C757D] mt-1">Agent of record: <b>{rec.signing_agent_name || "—"}</b></div>
              </div>
            </div>
            <div className="text-sm text-[#0B132B] leading-relaxed">
              Testing has completed configuration. As the agent of record, log into the dealer portal
              and verify everything looks correct end-to-end. Approve to hand off to Dealer Install, or
              return to Production with a documented list of changes. Rework will loop back here for
              your re-approval before advancing.
            </div>
            <div className="mt-3 text-[10px] text-[#6C757D] italic">
              Approve / Request Change buttons are in the Corrections panel above.
            </div>
          </Card>
        )}

        {/* --- DEALER INSTALL --- */}
        {rec.status === "dealer_install" && (
          <Card className="p-5 border-[#E9ECEF] rounded-sm mb-4" data-testid="stage-install-card">
            <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
              <div>
                <div className="text-xs uppercase tracking-[0.22em] font-bold text-yellow-800">Dealer Install</div>
                <div className="text-[11px] text-[#6C757D] mt-1">Assigned agent: <b>{rec.assigned_install_user_name || "—"}</b></div>
              </div>
              {(isAgent || isAgencyAdmin || isAdmin) && (
                <div className="flex gap-2">
                  <Button variant="outline" onClick={() => doAction("Install kickoff saved",
                      () => api.post(`/onboarding/${id}/start-dealer-install`, installForm))}
                    className="h-10 rounded-sm" data-testid="btn-start-dealer-install">
                    Save install kickoff
                  </Button>
                  <Button onClick={() => doAction("Install marked complete",
                      () => api.post(`/onboarding/${id}/install-complete`))}
                    className="h-10 rounded-sm bg-green-700 hover:bg-green-800 text-white" data-testid="btn-install-complete">
                    <CheckCircle2 size={14} className="mr-1.5"/> Install Complete
                  </Button>
                </div>
              )}
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Install date</Label>
                <Input type="date" value={installForm.install_date} onChange={(e) => setInstallForm({ ...installForm, install_date: e.target.value })} className="mt-1 h-10 rounded-sm" data-testid="install-date"/>
              </div>
              <div>
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Training date</Label>
                <Input type="date" value={installForm.training_date} onChange={(e) => setInstallForm({ ...installForm, training_date: e.target.value })} className="mt-1 h-10 rounded-sm" data-testid="training-date"/>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Dealer contacts trained</Label>
                <Input value={installForm.dealer_contacts_trained} onChange={(e) => setInstallForm({ ...installForm, dealer_contacts_trained: e.target.value })} placeholder="Comma-separated names" className="mt-1 h-10 rounded-sm" data-testid="dealer-contacts-trained"/>
              </div>
              <div className="sm:col-span-2">
                <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Notes</Label>
                <Input value={installForm.notes} onChange={(e) => setInstallForm({ ...installForm, notes: e.target.value })} placeholder="Any issues, feedback, or dealer requests" className="mt-1 h-10 rounded-sm" data-testid="install-notes"/>
              </div>
            </div>
          </Card>
        )}

        {rec.status === "install_complete" && (
          <Card className="p-5 border-green-200 bg-green-50 rounded-sm mb-4 flex items-center gap-3" data-testid="stage-complete-card">
            <CheckCircle2 size={20} className="text-green-700"/>
            <div>
              <div className="font-bold text-green-800">Onboarding complete</div>
              <div className="text-xs text-green-700 mt-0.5">Archives automatically after 7 days (Phase 5).</div>
            </div>
          </Card>
        )}

        {/* --- Products summary --- */}
        <Card className="p-5 border-[#E9ECEF] rounded-sm mb-4" data-testid="products-summary-card">
          <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#0B132B] mb-3">Products selected</div>
          {(rec.products || []).length === 0 ? (
            <div className="text-xs italic text-[#6C757D]">No products selected yet.</div>
          ) : (
            <div className="flex flex-wrap gap-2">
              {rec.products.map((p) => (
                <span key={p.product_id} className="text-[11px] px-2 py-1 rounded-sm bg-[#F8F9FA] border border-[#E9ECEF] text-[#0B132B]" data-testid={`product-chip-${p.product_key}`}>
                  <Package size={10} className="inline mr-1"/> {p.product_name} <span className="text-[#6C757D]">· {p.vertical_key}</span>
                </span>
              ))}
            </div>
          )}
        </Card>

        {/* --- Activity log --- */}
        <Card className="p-5 border-[#E9ECEF] rounded-sm" data-testid="activity-log-card">
          <button type="button" onClick={() => setActivityOpen((o) => !o)} className="w-full text-xs uppercase tracking-[0.22em] font-bold text-[#0B132B] flex items-center justify-between gap-2" data-testid="activity-toggle">
            <span className="flex items-center gap-2"><History size={12}/> Activity <span className="normal-case tracking-normal font-normal text-[#6C757D]">({(rec.history || []).length} updates)</span></span>
            <span className="flex items-center gap-2 text-[10px] text-[#6C757D] normal-case tracking-normal font-normal">{!activityOpen && rec.history?.[0] && <span className="hidden sm:inline truncate max-w-[360px]">Latest: {new Date(rec.history[0].created_at).toLocaleString()} — {rec.history[0].actor_name} {prettyEvent(rec.history[0].event)}</span>}{activityOpen ? <ChevronUp size={14}/> : <ChevronDown size={14}/>}</span>
          </button>
          <div className={`space-y-2 mt-3 ${activityOpen ? "" : "hidden"}`} data-testid="activity-list">
            {(rec.history || []).map((h) => (
              <div key={h.id || h.created_at + h.event} className="text-xs text-[#0B132B] flex items-start gap-2 border-l-2 border-[#D4AF37] pl-3">
                <div className="w-40 shrink-0 text-[#6C757D]">{new Date(h.created_at).toLocaleString()}</div>
                <div className="flex-1">
                  <b>{h.actor_name}</b> — {prettyEvent(h.event)} {h.from_status && h.to_status && <span className="text-[#6C757D]">({h.from_status.replace(/_/g," ")} → {h.to_status.replace(/_/g," ")})</span>}
                  {h.detail && Object.keys(h.detail).length > 0 && <div className="text-[10px] text-[#6C757D] mt-0.5">{summarizeDetail(h.detail)}</div>}
                </div>
              </div>
            ))}
            {(rec.history || []).length === 0 && <div className="text-xs italic text-[#6C757D]">No events yet.</div>}
          </div>
        </Card>
      </main>

      {/* Return-to-production reason dialog */}
      <Dialog open={returnDlg.open} onOpenChange={(o) => setReturnDlg({ ...returnDlg, open: o })}>
        <DialogContent data-testid="return-dialog">
          <DialogHeader>
            <DialogTitle>Return to Production</DialogTitle>
            <DialogDescription>Testing results are preserved. The Production Owner will see this reason on the record.</DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Reason (required)</Label>
            <Input value={returnDlg.reason} onChange={(e) => setReturnDlg({ ...returnDlg, reason: e.target.value })}
              className="mt-1 h-10 rounded-sm" placeholder="What needs to change?" data-testid="return-reason-input"/>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnDlg({ open: false, reason: "" })} className="h-10 rounded-sm">Cancel</Button>
            <Button onClick={async () => {
              if (!returnDlg.reason.trim()) { toast.error("Reason required"); return; }
              await doAction("Returned to production", () => api.post(`/onboarding/${id}/return-to-production`, { reason: returnDlg.reason }));
              setReturnDlg({ open: false, reason: "" });
            }} className="h-10 rounded-sm bg-red-700 hover:bg-red-800 text-white" data-testid="btn-confirm-return">
              Return to Production
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin stage move (drag-drop or checklist override) */}
      <Dialog open={moveDlg.open} onOpenChange={(o) => setMoveDlg({ ...moveDlg, open: o })}>
        <DialogContent data-testid="move-stage-dialog">
          <DialogHeader>
            <DialogTitle>{moveDlg.override ? "Override testing checklist?" : `Move to ${STAGE_LABEL[moveDlg.to] || moveDlg.to}`}</DialogTitle>
            <DialogDescription>
              {moveDlg.override
                ? <>The checklist is not complete ({moveDlg.blocker}). As super admin you can move this dealer to <b>Agent Review</b> anyway. This is logged with your name and reason.</>
                : <>{STAGE_ORDER.indexOf(moveDlg.to) < STAGE_ORDER.indexOf(rec.status) ? "Sending this dealer back to an earlier stage." : "Advancing this dealer, skipping the normal gate."} Current stage: <b>{STAGE_LABEL[rec.status] || rec.status}</b>. The reason is recorded in the activity log.</>}
            </DialogDescription>
          </DialogHeader>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#0B132B]">Reason / comment (required)</Label>
            <Input value={moveDlg.reason} onChange={(e) => setMoveDlg({ ...moveDlg, reason: e.target.value })} autoFocus
              className="mt-1 h-10 rounded-sm" placeholder={moveDlg.override ? "Why is it safe to proceed without the checklist?" : "Why is this dealer moving?"} data-testid="move-reason-input"/>
          </div>
          {!moveDlg.override && STAGE_ORDER.indexOf(moveDlg.to) < STAGE_ORDER.indexOf(rec.status) && (
            <label className="flex items-start gap-2 text-xs text-[#0B132B]"><input type="checkbox" checked={moveDlg.mode === "reset"} onChange={(e) => setMoveDlg({ ...moveDlg, mode: e.target.checked ? "reset" : "keep" })} className="mt-0.5" data-testid="move-reset-checkbox"/> Reset downstream work (assignments, timestamps, install statuses) so the pipeline restarts from this stage</label>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setMoveDlg({ ...moveDlg, open: false })} className="h-10 rounded-sm">Cancel</Button>
            <Button onClick={async () => {
              if (!moveDlg.reason.trim()) { toast.error("Reason required"); return; }
              await doAction(moveDlg.override ? "Checklist overridden — moved to Agent Review" : `Moved to ${STAGE_LABEL[moveDlg.to] || moveDlg.to}`,
                () => api.post(`/onboarding/${id}/move-stage`, { to_stage: moveDlg.to, reason: (moveDlg.override ? "[Checklist override] " : "") + moveDlg.reason.trim(), mode: moveDlg.mode }));
              setMoveDlg({ ...moveDlg, open: false });
            }} className={`h-10 rounded-sm text-white ${moveDlg.override ? "bg-amber-600 hover:bg-amber-700" : "bg-[#0B132B] hover:bg-[#1C2541]"}`} data-testid="btn-confirm-move">
              {moveDlg.override ? "I accept — override and move" : "Confirm move"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ReassignDealerDialog
        open={reassignOpen}
        onboardingId={id}
        currentRecord={rec}
        onOpenChange={setReassignOpen}
        onReassigned={load}
      />
    </div>
  );
}

// -----------------------------------------------------------------------------
// Production Rate Cards panel — internal only. The backend matches each of the
// dealer's program lines to the production workbooks explicitly linked to that
// program (vertical + catalog product) on Admin → Production rate cards.
// -----------------------------------------------------------------------------
function ProductionRateCardsPanel({ onboardingId }) {
  const [data, setData] = useState({ lines: [], unlinked: [] });
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/onboarding/${onboardingId}/production-rate-cards`);
        if (!cancelled) setData(data || { lines: [], unlinked: [] });
      } catch {
        if (!cancelled) setData({ lines: [], unlinked: [] });
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [onboardingId]);
  const cards = [...data.unlinked, ...data.lines.flatMap((l) => l.cards)];

  const download = async (c) => {
    try {
      const { data, headers } = await api.get(`/admin/rate-library/sheets/${c.id}/download`, {
        responseType: "blob",
        params: { disposition: "attachment" },
      });
      const cd = headers["content-disposition"] || "";
      const match = /filename\*?=(?:UTF-8'')?"?([^;"]+)"?/i.exec(cd);
      const fn = (match && decodeURIComponent(match[1])) || c.filename || "workbook.xlsx";
      const url = URL.createObjectURL(new Blob([data], { type: headers["content-type"] || "application/octet-stream" }));
      const a = document.createElement("a");
      a.href = url; a.download = fn; a.click();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Download failed");
    }
  };

  const perProduct = data.lines.map((l) => ({ product: l, matches: l.cards }));
  const unmatched = data.unlinked;

  return (
    <Card className="p-5 border-[#0B132B]/20 rounded-sm mb-4 bg-[#0B132B]/[0.02]" data-testid="production-rate-cards-panel">
      <div className="flex items-center gap-2 mb-3">
        <span className="text-[9px] uppercase tracking-wider font-bold text-white bg-[#0B132B] px-1.5 py-0.5 rounded-sm">Internal</span>
        <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#0B132B]">Production Rate Cards</div>
        <span className="text-[10px] text-[#6C757D]">— master install workbooks per program</span>
      </div>
      {loading ? (
        <div className="text-sm text-[#6C757D] py-4">Loading…</div>
      ) : cards.length === 0 ? (
        <div className="text-sm text-[#6C757D] italic py-4">
          No production rate cards uploaded yet. Add them at
          {" "}<Link to="/admin/production-rate-cards" className="underline text-[#0B132B] font-semibold">Admin → Production rate cards</Link>.
        </div>
      ) : (
        <div className="space-y-3" data-testid="production-rate-cards-list">
          {perProduct.map(({ product, matches }, idx) => (
            <div key={product.line_id || product.product_id || idx} className="border border-[#E9ECEF] rounded-sm p-2.5 bg-white" data-testid={`prod-card-line-${product.line_id || idx}`}>
              <div className="flex items-center gap-2 mb-1.5">
                <div className="text-sm font-bold text-[#0B132B] truncate">{product.product_name || product.product_key}</div>
                {product.vertical_key && <span className="text-[9px] uppercase tracking-wider text-[#6C757D]">{product.vertical_key}</span>}
                <span className="text-[9px] uppercase tracking-wider text-[#6C757D] font-semibold">
                  {matches.length ? `${matches.length} matching workbook${matches.length === 1 ? "" : "s"}` : "no matching workbook"}
                </span>
              </div>
              {matches.length === 0 ? (
                <div className="text-[11px] text-[#6C757D] italic">
                  No production workbook is linked to this program yet — link one under{" "}
                  <Link to="/admin/production-rate-cards" className="underline text-[#0B132B] font-semibold">Admin → Production rate cards</Link>.
                </div>
              ) : (
                <div className="flex flex-wrap gap-1.5">
                  {matches.map((m) => (
                    <button
                      key={m.id}
                      type="button"
                      onClick={() => download(m)}
                      className="h-8 px-3 text-xs font-bold rounded-sm bg-[#0B132B] text-white hover:bg-[#1C2541] inline-flex items-center gap-1.5"
                      data-testid={`prod-card-download-${m.id}`}
                    >
                      <Download size={11} /> {m.program_name}
                    </button>
                  ))}
                </div>
              )}
            </div>
          ))}
          {unmatched.length > 0 && (
            <details className="border border-dashed border-[#E9ECEF] rounded-sm p-2.5" data-testid="production-rate-cards-unmatched">
              <summary className="text-[11px] text-[#6C757D] cursor-pointer">
                Other production workbooks ({unmatched.length}) — not linked to this dealer's programs
              </summary>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {unmatched.map((m) => (
                  <button key={m.id} type="button" onClick={() => download(m)}
                    className="h-7 px-2.5 text-[11px] font-semibold rounded-sm border border-[#E9ECEF] hover:border-[#0B132B]/40 inline-flex items-center gap-1"
                    data-testid={`prod-card-download-other-${m.id}`}>
                    <Download size={10} /> {m.program_name}
                  </button>
                ))}
              </div>
            </details>
          )}
        </div>
      )}
    </Card>
  );
}


function prettyEvent(e) {
  return (e || "").replace(/_/g, " ");
}
function summarizeDetail(d) {
  if (!d || typeof d !== "object") return "";
  if (d.reason) return `Reason: ${d.reason}`;
  if (d.assigned_to) return `Assigned to ${d.assigned_to}`;
  if (d.product_names) return `${d.product_names.length} products: ${d.product_names.slice(0, 3).join(", ")}${d.product_names.length > 3 ? "…" : ""}`;
  if (d.product_name && d.from && d.to) return `${d.product_name}: ${d.from} → ${d.to}`;
  return "";
}

function ProductRow({ onboardingId, product, editable, onSaved }) {
  const [status, setStatus] = useState(product.install_status || "not_started");
  const [notes, setNotes] = useState(product.install_notes || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setStatus(product.install_status || "not_started"); setNotes(product.install_notes || ""); }, [product.install_status, product.install_notes]);
  // Prefer per-row line_id so multi-vertical picks (e.g. VSC for Automotive
  // AND Powersports) each save independently. Fall back to product_id for
  // legacy records that pre-date the line_id migration — the backend also
  // fans out legacy product_id hits across every matching row.
  const rowKey = product.line_id || product.product_id;
  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/onboarding/${onboardingId}/production/product/${rowKey}`, {
        install_status: status, install_notes: notes,
      });
      toast.success("Saved");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const doneColor = status === "installed" ? "bg-green-50 border-green-300" :
                    status === "needs_review" ? "bg-red-50 border-red-300" :
                    status === "in_progress" ? "bg-orange-50 border-orange-300" :
                    "bg-white border-[#E9ECEF]";
  return (
    <div className={`p-3 border rounded-sm ${doneColor}`} data-testid={`product-row-${product.line_id || product.product_key}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="font-bold text-sm text-[#0B132B]">{product.product_name} <span className="text-[10px] text-[#6C757D] uppercase tracking-[0.16em] ml-2">{product.vertical_key}</span></div>
        {editable && (
          <div className="flex items-center gap-2">
            <select value={status} onChange={(e) => setStatus(e.target.value)} className="h-9 rounded-sm border border-[#E9ECEF] text-xs px-2" data-testid={`product-status-${product.product_key}`}>
              {INSTALL_STATUSES.map(s => <option key={s.key} value={s.key}>{s.label}</option>)}
            </select>
            <Button size="sm" onClick={save} disabled={saving} className="h-9 rounded-sm text-xs bg-[#0B132B] hover:bg-[#1C2541] text-white">Save</Button>
          </div>
        )}
        {!editable && <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">{status.replace(/_/g," ")}</span>}
      </div>
      {editable && (
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Setup notes, dealer number, program IDs…" className="mt-2 h-9 rounded-sm text-xs" data-testid={`product-notes-${product.product_key}`}/>
      )}
      {!editable && notes && <div className="text-[11px] text-[#6C757D] mt-1">{notes}</div>}
    </div>
  );
}

function SubscriptionUrlField({ onboardingId, initialUrl, initialConfig, editable, onSaved }) {
  const cfg0 = initialConfig || {};
  const [url, setUrl] = useState(initialUrl || "");
  const [commAgency, setCommAgency] = useState(cfg0.subscription_commission_agency ?? "");
  const [commAgent, setCommAgent] = useState(cfg0.subscription_commission_agent ?? "");
  const [commAwg, setCommAwg] = useState(cfg0.subscription_commission_awg ?? "");
  const [commMode, setCommMode] = useState(cfg0.subscription_commission_mode || "percent");
  const [markup, setMarkup] = useState(cfg0.subscription_retail_markup ?? "");
  const [markupMode, setMarkupMode] = useState(cfg0.subscription_retail_markup_mode || "percent");
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    setUrl(initialUrl || "");
    const c = initialConfig || {};
    setCommAgency(c.subscription_commission_agency ?? "");
    setCommAgent(c.subscription_commission_agent ?? "");
    setCommAwg(c.subscription_commission_awg ?? "");
    setCommMode(c.subscription_commission_mode || "percent");
    setMarkup(c.subscription_retail_markup ?? "");
    setMarkupMode(c.subscription_retail_markup_mode || "percent");
  }, [initialUrl, initialConfig]);

  const toNum = (v) => (v === "" || v === null || v === undefined ? null : Number(v));
  const commTotal = commMode === "percent"
    ? [commAgency, commAgent, commAwg].map(toNum).filter((n) => n != null).reduce((a, b) => a + b, 0)
    : null;
  const commWarn = commMode === "percent" && commTotal > 100;

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/onboarding/${onboardingId}/subscription-url`, {
        subscription_landing_url: url,
        subscription_commission_agency: toNum(commAgency),
        subscription_commission_agent: toNum(commAgent),
        subscription_commission_awg: toNum(commAwg),
        subscription_commission_mode: commMode,
        subscription_retail_markup: toNum(markup),
        subscription_retail_markup_mode: markupMode,
      });
      toast.success("Subscription VSC saved");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const sym = (m) => (m === "percent" ? "%" : "$");
  return (
    <div className="mt-4 p-4 border border-[#D4AF37] bg-[#D4AF37]/5 rounded-sm space-y-4" data-testid="subscription-url-field">
      <div>
        <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#D4AF37] mb-1">
          Subscription VSC landing page
        </div>
        <div className="text-xs text-[#6C757D] mb-2">
          Paste the dealer's landing-page URL — it goes into the install-handoff emails.
        </div>
        <Input
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://…"
          disabled={!editable}
          className="h-9 rounded-sm text-xs"
          data-testid="subscription-url-input"
        />
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#D4AF37]">
            Commission split
          </div>
          {editable && (
            <div className="flex text-[10px] font-mono uppercase tracking-[0.14em] rounded-sm overflow-hidden border border-[#E9ECEF]">
              {["percent", "flat"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setCommMode(m)}
                  className={`px-2 py-1 ${commMode === m ? "bg-[#0B132B] text-white" : "bg-white text-[#6C757D]"}`}
                  data-testid={`subscription-comm-mode-${m}`}
                >
                  {m === "percent" ? "%" : "$"}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="grid grid-cols-3 gap-2">
          {[
            ["Agency", commAgency, setCommAgency, "agency"],
            ["Agent", commAgent, setCommAgent, "agent"],
            ["AWG", commAwg, setCommAwg, "awg"],
          ].map(([label, val, setter, key]) => (
            <div key={key}>
              <div className="text-[10px] uppercase tracking-[0.16em] font-bold text-[#6C757D] mb-1">{label}</div>
              <div className="relative">
                <Input
                  type="number"
                  value={val}
                  onChange={(e) => setter(e.target.value)}
                  placeholder="0"
                  disabled={!editable}
                  className="h-9 rounded-sm text-xs pr-6"
                  data-testid={`subscription-comm-${key}`}
                />
                <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[#6C757D]">
                  {sym(commMode)}
                </span>
              </div>
            </div>
          ))}
        </div>
        {commMode === "percent" && commTotal != null && (commAgency !== "" || commAgent !== "" || commAwg !== "") && (
          <div className={`mt-2 text-[10px] uppercase tracking-[0.14em] font-bold ${commWarn ? "text-red-600" : "text-[#6C757D]"}`}>
            Total: {commTotal.toFixed(2)}%{commWarn ? " — over 100%" : ""}
          </div>
        )}
      </div>

      <div>
        <div className="flex items-center justify-between mb-2">
          <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-[#D4AF37]">
            Dealer retail markup
          </div>
          {editable && (
            <div className="flex text-[10px] font-mono uppercase tracking-[0.14em] rounded-sm overflow-hidden border border-[#E9ECEF]">
              {["percent", "flat"].map((m) => (
                <button
                  key={m}
                  type="button"
                  onClick={() => setMarkupMode(m)}
                  className={`px-2 py-1 ${markupMode === m ? "bg-[#0B132B] text-white" : "bg-white text-[#6C757D]"}`}
                  data-testid={`subscription-markup-mode-${m}`}
                >
                  {m === "percent" ? "%" : "$"}
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="relative w-40">
          <Input
            type="number"
            value={markup}
            onChange={(e) => setMarkup(e.target.value)}
            placeholder="0"
            disabled={!editable}
            className="h-9 rounded-sm text-xs pr-6"
            data-testid="subscription-markup-input"
          />
          <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[11px] text-[#6C757D]">
            {sym(markupMode)}
          </span>
        </div>
      </div>

      {editable && (
        <div className="flex items-center justify-end">
          <Button
            size="sm"
            onClick={save}
            disabled={saving}
            className="h-9 rounded-sm text-xs bg-[#0B132B] hover:bg-[#1C2541] text-white"
            data-testid="subscription-url-save"
          >
            {saving ? "Saving…" : "Save Subscription VSC"}
          </Button>
        </div>
      )}
    </div>
  );
}

function ChecklistRow({ onboardingId, item, editable, onSaved }) {
  const [result, setResult] = useState(item.result || null);
  const [notes, setNotes] = useState(item.notes || "");
  const [saving, setSaving] = useState(false);
  useEffect(() => { setResult(item.result || null); setNotes(item.notes || ""); }, [item.result, item.notes]);
  const save = async (val = result) => {
    setSaving(true);
    try {
      await api.put(`/onboarding/${onboardingId}/testing/item/${item.key}`, { result: val, notes });
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };
  const tone = result === "pass" ? "bg-green-50 border-green-300" :
               result === "fail" ? "bg-red-50 border-red-300" :
               result === "na" ? "bg-slate-50 border-slate-300" :
               "bg-white border-[#E9ECEF]";
  return (
    <div className={`p-3 border rounded-sm ${tone}`} data-testid={`checklist-row-${item.key}`}>
      <div className="flex items-center justify-between gap-3">
        <div className="font-medium text-sm text-[#0B132B]">{item.label}</div>
        {editable ? (
          <div className="flex gap-1">
            {[
              { k: "pass", ic: CheckCircle2, cls: "hover:bg-green-100 text-green-700", active: "bg-green-600 text-white" },
              { k: "fail", ic: XCircle,     cls: "hover:bg-red-100 text-red-700",     active: "bg-red-600 text-white" },
              { k: "na",   ic: MinusCircle, cls: "hover:bg-slate-100 text-slate-700", active: "bg-slate-600 text-white" },
            ].map(({ k, ic: Ic, cls, active }) => (
              <button key={k} type="button" onClick={() => { setResult(k); save(k); }}
                className={`h-8 w-8 rounded-sm flex items-center justify-center border border-[#E9ECEF] ${result === k ? active : `bg-white ${cls}`}`}
                data-testid={`checklist-${item.key}-${k}`}>
                <Ic size={14}/>
              </button>
            ))}
          </div>
        ) : (
          <span className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">{result || "pending"}</span>
        )}
      </div>
      {editable && (
        <Input value={notes} onChange={(e) => setNotes(e.target.value)} onBlur={() => save()} placeholder="Notes (optional)" className="mt-2 h-9 rounded-sm text-xs" data-testid={`checklist-notes-${item.key}`} disabled={saving}/>
      )}
      {!editable && notes && <div className="text-[11px] text-[#6C757D] mt-1">{notes}</div>}
    </div>
  );
}

// ---- Phase-A ACH parallel-track section -----------------------------------
const ACH_STATUS_META = {
  not_submitted:         { label: "Not Submitted",       tone: "bg-[#F1F3F5] text-[#495057] border-[#DEE2E6]" },
  submitted:             { label: "Submitted",           tone: "bg-blue-50 text-blue-700 border-blue-200" },
  verification_pending:  { label: "Verification Pending", tone: "bg-orange-50 text-orange-800 border-orange-300" },
  awaiting_verification: { label: "Awaiting Verification", tone: "bg-orange-50 text-orange-800 border-orange-300" },
  verified:              { label: "Verified",            tone: "bg-green-50 text-green-700 border-green-300" },
  issue:                 { label: "Needs Attention",     tone: "bg-red-50 text-red-700 border-red-300" },
};
const ACH_AGING_LABEL = {
  processing:            "0–24h · Processing",
  verification_pending:  "24–48h · Verification Pending",
  follow_up_recommended: "48–72h · Follow-Up Recommended",
  needs_attention:       "72h+ · Needs Attention",
};

function AchSection({ rec, onboardingId, editable, onSaved }) {
  const contact = rec.accounting_contact || {};
  const ach = rec.ach || { status: "not_submitted" };
  const meta = ACH_STATUS_META[ach.status] || ACH_STATUS_META.not_submitted;
  const [form, setForm] = useState({
    first_name: contact.first_name || "",
    last_name:  contact.last_name  || "",
    email:      contact.email      || "",
    phone:      contact.phone      || "",
    job_title:  contact.job_title  || "",
  });
  const [busy, setBusy] = useState(false);
  const [issueDlg, setIssueDlg] = useState(false);
  const [issueReason, setIssueReason] = useState("");

  const saveContact = async () => {
    if (!form.first_name || !form.last_name || !form.email) {
      toast.error("First name, last name, and email are required"); return;
    }
    setBusy(true);
    try {
      await api.put(`/onboarding/${onboardingId}/accounting-contact`, form);
      toast.success("Accounting contact saved");
      onSaved?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };

  const doAction = async (label, fn) => {
    setBusy(true);
    try { await fn(); toast.success(label); onSaved?.(); }
    catch (e) { toast.error(e.response?.data?.detail || `${label} failed`); }
    finally { setBusy(false); }
  };

  return (
    <Card className="p-5 border-[#E9ECEF] rounded-sm mb-4" data-testid="ach-section">
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="flex items-center gap-2">
            <Landmark size={14} className="text-[#0B132B]" />
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-[#0B132B]">ACH / Accounting Setup</div>
            <span className={`px-2 py-0.5 rounded-sm border text-[10px] uppercase tracking-[0.14em] font-bold ${meta.tone}`} data-testid="ach-status-pill">
              {meta.label}
            </span>
            {rec.ach_aging_bucket && (
              <span className="text-[10px] text-[#6C757D] italic flex items-center gap-1">
                <Clock size={10} /> {ACH_AGING_LABEL[rec.ach_aging_bucket]}
              </span>
            )}
          </div>
          <div className="text-[11px] text-[#6C757D] mt-1 italic">
            Runs in parallel — Production continues while microdeposit verification (24–48h) completes.
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <div>
          <Label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">First name</Label>
          <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="h-9 rounded-sm mt-1" disabled={!editable || busy} data-testid="ach-first-name" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Last name</Label>
          <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="h-9 rounded-sm mt-1" disabled={!editable || busy} data-testid="ach-last-name" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="h-9 rounded-sm mt-1" disabled={!editable || busy} data-testid="ach-email" />
        </div>
        <div>
          <Label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Phone (optional)</Label>
          <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="h-9 rounded-sm mt-1" disabled={!editable || busy} data-testid="ach-phone" />
        </div>
        <div className="md:col-span-2">
          <Label className="text-[10px] uppercase tracking-[0.14em] font-bold text-[#6C757D]">Job title (optional)</Label>
          <Input value={form.job_title} onChange={(e) => setForm({ ...form, job_title: e.target.value })} className="h-9 rounded-sm mt-1" disabled={!editable || busy} data-testid="ach-job-title" />
        </div>
      </div>

      {editable && (
        <div className="flex flex-wrap items-center gap-2 mt-4 pt-4 border-t border-[#F1F3F5]">
          <Button size="sm" variant="outline" onClick={saveContact} disabled={busy} className="rounded-sm h-9 text-[11px] uppercase tracking-[0.14em] font-bold" data-testid="btn-save-accounting-contact">
            Save contact
          </Button>
          <Button size="sm" onClick={() => doAction("ACH submitted for verification", () => api.post(`/onboarding/${onboardingId}/ach/submit`))}
                  disabled={busy || !form.email || ach.status === "verified"}
                  className="rounded-sm h-9 bg-[#0B132B] hover:bg-[#1C2541] text-white text-[11px] uppercase tracking-[0.14em] font-bold"
                  data-testid="btn-submit-ach">
            <Send size={12} className="mr-1.5" /> Submit ACH for Verification
          </Button>
          {ach.status === "verification_pending" && (
            <Button size="sm" variant="outline" onClick={() => doAction("Verification email resent", () => api.post(`/onboarding/${onboardingId}/ach/resend-email`))}
                    disabled={busy} className="rounded-sm h-9 text-[11px] uppercase tracking-[0.14em] font-bold" data-testid="btn-resend-ach-email">
              <Mail size={12} className="mr-1.5" /> Resend email
            </Button>
          )}
          {ach.status !== "verified" && ach.submitted_at && (
            <Button size="sm" variant="outline" onClick={() => doAction("Marked verified", () => api.post(`/onboarding/${onboardingId}/ach/mark-verified`, { notes: "" }))}
                    disabled={busy} className="rounded-sm h-9 text-[11px] uppercase tracking-[0.14em] font-bold border-green-400 text-green-700 hover:bg-green-50" data-testid="btn-mark-ach-verified">
              <CheckCircle2 size={12} className="mr-1.5" /> Mark Verified
            </Button>
          )}
          {ach.submitted_at && ach.status !== "verified" && (
            <Button size="sm" variant="outline" onClick={() => setIssueDlg(true)}
                    disabled={busy} className="rounded-sm h-9 text-[11px] uppercase tracking-[0.14em] font-bold border-red-300 text-red-700 hover:bg-red-50" data-testid="btn-flag-ach-issue">
              <AlertTriangle size={12} className="mr-1.5" /> Flag Issue
            </Button>
          )}
        </div>
      )}

      {(ach.submitted_at || ach.verified_at) && (
        <div className="text-[10px] text-[#6C757D] mt-3 flex flex-wrap gap-x-4 gap-y-1" data-testid="ach-audit-line">
          {ach.submitted_at && <span>Submitted {formatShort(ach.submitted_at)} by <b>{ach.submitted_by_name}</b></span>}
          {ach.last_email_sent_at && <span>Email {formatShort(ach.last_email_sent_at)}{ach.resend_count > 0 ? ` (resent ${ach.resend_count}×)` : ""}</span>}
          {ach.verified_at && <span>Verified {formatShort(ach.verified_at)} by <b>{ach.verified_by_name}</b></span>}
          {ach.status === "issue" && ach.issue_reason && <span className="text-red-700">Issue: {ach.issue_reason}</span>}
        </div>
      )}

      <Dialog open={issueDlg} onOpenChange={setIssueDlg}>
        <DialogContent className="sm:max-w-md" data-testid="ach-issue-dialog">
          <DialogHeader>
            <DialogTitle>Flag ACH Issue</DialogTitle>
            <DialogDescription>What went wrong? This is logged and shown alongside the record.</DialogDescription>
          </DialogHeader>
          <Input value={issueReason} onChange={(e) => setIssueReason(e.target.value)} placeholder="Bank rejected microdeposits, wrong routing…" data-testid="ach-issue-reason" />
          <DialogFooter>
            <Button variant="outline" onClick={() => setIssueDlg(false)}>Cancel</Button>
            <Button onClick={async () => {
              if (!issueReason.trim()) { toast.error("Reason required"); return; }
              setIssueDlg(false);
              await doAction("Issue flagged", () => api.post(`/onboarding/${onboardingId}/ach/mark-issue`, { reason: issueReason.trim() }));
              setIssueReason("");
            }} className="bg-red-600 hover:bg-red-700 text-white" data-testid="ach-issue-save">Flag</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Card>
  );
}

function formatShort(iso) {
  if (!iso) return "";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
}
